// offline-store.ts — the two on-device databases behind backlit.offline.
//
//   backlit-offline-mirror (v1) — SERVER-ACKNOWLEDGED state only, never an
//     optimistic local write:
//       entries  keyPath [s,p,k]       → MirrorEntry  (index accessedAt, for LRU)
//       coverage keyPath [s,p,prefix]  → CoverageRecord ("this whole prefix is known")
//       meta     out-of-line keys      → the identity record, the remembered user,
//                                        lastSyncedAt and the bytesUsed counter
//     `s` is the store (data|userdata|records), `p` the partition ("g" for
//     glow-shared data on Public/Accounts, "u:<uid>" per user — see
//     offline-identity.ts partitionFor).
//
//   Entry states: "present" (bytes, or metadata-only),
//   "absent" (a tombstone: the server said 404) and "unknown" (nothing
//   reliable — never infer absence). A key under a complete coverage prefix
//   with NO entry reads as known absent, so only a server "absent" (a 404
//   tombstone, an acknowledged delete, a list-page prune) may remove or
//   tombstone an entry. Every other loss keeps an entry: LRU eviction and an
//   oversize put demote a covered key to metadata-only, and invalidate turns a
//   key into "unknown" (an uncovered key may simply be deleted — no entry is
//   already unknown there).
//
//   Evidence ordering: every fold carries the
//   request interval of its evidence — seenFrom (request start) to seenTo
//   (response) — and reconcile() orders it against the stored entry by that
//   interval, never by when the fold happens to run: strictly older evidence
//   loses, strictly newer wins, and overlapping evidence that disagrees becomes
//   "unknown" (never "the newer crc", which could be a false CAS base). An entry
//   without an interval reads as [0, fetchedAt]. A list page prunes only
//   entries strictly older than the page, and records its prefix complete only
//   when nothing was skipped and no unknown entry is left in its range.
//
//   backlit-offline-outbox (v1) — queued writes, touched ONLY by the SDK. It is
//     a separate database so the service worker / logout page can delete the
//     mirror by name without ever reaching the queue (SEC-2):
//       ops keyPath seq (autoIncrement) → OutboxOp
//           (index uidStoreKey [uid,store,key], index state)
//
// MUST-MATCH: MIRROR_DB_NAME is deleted by name from cmd/edge/backlit-sw.js and
// the edge logout page (pinned by sdk/test/offline-mustmatch.test.ts). The SW
// must never reference OUTBOX_DB_NAME.
//
// Robustness rules:
//   - every operation opens a connection, runs exactly one transaction and
//     closes on complete/error/abort — no connection is held between calls, and
//     an open connection closes itself on `versionchange`, so the SW, the
//     logout page or a newer SDK build can always delete/upgrade the database;
//   - nothing but IDB request promises is awaited inside a transaction (values
//     arrive as ArrayBuffers, so there is no Blob read to do mid-transaction);
//   - a VersionError (the database exists at a newer version) marks the store
//     unavailable for the life of the store object and NEVER deletes anything
//     (READ-3) — only an explicit destroy() deletes a database;
//   - deleteDatabase never rejects and never hangs past its timeout.
//
// Each database has an IndexedDB implementation and an in-memory one with
// identical semantics (tests, and injected stores). They are separate named
// exports so an unused implementation tree-shakes out of sdk.js.

import { now, type MirrorStoreName } from "./offline-identity.js";

export const MIRROR_DB_NAME = "backlit-offline-mirror";
export const MIRROR_DB_VERSION = 1;
export const OUTBOX_DB_NAME = "backlit-offline-outbox";
export const OUTBOX_DB_VERSION = 1;

// Object store names.
export const STORE_ENTRIES = "entries";
export const STORE_COVERAGE = "coverage";
export const STORE_META = "meta";
export const STORE_OPS = "ops";

// Index names.
export const IDX_ACCESSED_AT = "accessedAt";
export const IDX_UID_STORE_KEY = "uidStoreKey";
export const IDX_STATE = "state";

// Caps. DEFAULT_MAX_BYTES bounds the mirror's stored value bytes (LRU-evicted
// past it); the outbox refuses a write past either of its caps.
export const DEFAULT_MAX_BYTES = 52_428_800;
export const OUTBOX_MAX_OPS = 2000;
export const OUTBOX_MAX_BYTES = 52_428_800;

// CAPTURE_UID is the outbox `uid` of capture ops. uid is never empty: IndexedDB
// silently leaves a record out of a compound index when any part of its key is
// missing, which would hide capture ops from the [uid,store,key] index (CON-4).
export const CAPTURE_UID = "~capture";

// meta keys. META_BYTES, META_PINS and META_PINNED are maintained by the store
// itself — never set them. META_SYNCED is offline-sync's lastSyncedAt (so a
// whole-mirror purge resets it). META_PINS holds the pinned prefixes (Pin[]),
// META_PINNED the value bytes of the entries under any of them — both kept in
// the same transactions as META_BYTES (no schema change: an older bundle
// ignores them).
export const META_IDENTITY = "identity";
export const META_USER = "user";
export const META_BYTES = "bytesUsed";
export const META_SYNCED = "lastSyncedAt";
export const META_PINS = "pins";
export const META_PINNED = "pinnedBytes";

// How long a kept-back op survives (Outbox.sweepExpired): a quarantined op
// QUARANTINE_TTL_MS from quarantinedAt (createdAt when unset) — a revoked one
// REVOKED_TTL_MS; a held op HELD_TTL_MS from heldSince (createdAt when unset).
export const QUARANTINE_TTL_MS = 30 * 864e5;
export const REVOKED_TTL_MS = 7 * 864e5;
export const HELD_TTL_MS = 7 * 864e5;

const MIRROR_STORES: readonly MirrorStoreName[] = ["data", "userdata", "records"];
const BLOCKED_OPEN_MS = 3000;
const QUOTA_EXCEEDED = "QuotaExceededError";

// IdbEnv is the IndexedDB implementation a store uses: the factory plus the
// matching IDBKeyRange (both must come from the same implementation).
export interface IdbEnv {
  factory: IDBFactory;
  keyRange: typeof IDBKeyRange;
}

// defaultIdbEnv returns the page's IndexedDB, or null where there is none (or
// touching it throws — e.g. some sandboxed / privacy-restricted contexts).
export function defaultIdbEnv(): IdbEnv | null {
  try {
    const g = globalThis as { indexedDB?: IDBFactory; IDBKeyRange?: typeof IDBKeyRange };
    if (g.indexedDB && g.IDBKeyRange) return { factory: g.indexedDB, keyRange: g.IDBKeyRange };
  } catch {
    /* treat as unsupported */
  }
  return null;
}

// MirrorEntry is one mirrored key. state "absent" is a tombstone (the server
// said 404); a "present" entry may carry value null (metadata-only: listed, or
// its bytes evicted / never fetched); "unknown" means the device holds nothing
// reliable about the key (value null, crc32c "", never counted in bytesUsed) —
// readers give no answer for it and never infer absence. size is the
// server-reported size; only value.byteLength counts toward maxBytes.
// seenFrom / seenTo are the evidence's request interval (see reconcile).
export interface MirrorEntry {
  s: MirrorStoreName;
  p: string;
  k: string;
  state: "present" | "absent" | "unknown";
  value: ArrayBuffer | null;
  contentType: string;
  crc32c: string;
  size: number;
  owner?: string;
  fetchedAt: number;
  accessedAt: number;
  seenFrom?: number;
  seenTo?: number;
}

// MirrorMeta is a mirror entry without its value (MirrorStore.scanMeta):
// hasValue says whether the bytes are on the device; state "unknown" flags an
// entry the device knows nothing reliable about.
export type MirrorMeta = Omit<MirrorEntry, "value"> & { hasValue: boolean };

// PutResult is MirrorStore.put's outcome.
export type PutResult = "stored" | "meta" | "failed";

// Pin is one pinned prefix: LRU eviction never drops a value of partition p
// in store s whose key starts with prefix.
export interface Pin {
  s: MirrorStoreName;
  p: string;
  prefix: string;
  at: number;
}

// EvictReason says why a value left the device: "max_bytes" (LRU eviction to
// fit maxBytes), "no_room" (pinned values fill maxBytes, so an incoming value
// was kept metadata-only) or "quota" (the eviction after a storage-quota error).
export type EvictReason = "max_bytes" | "no_room" | "quota";

// Evicted is one value an eviction dropped (MirrorStore.onEvict).
export interface Evicted {
  s: MirrorStoreName;
  p: string;
  k: string;
  reason: EvictReason;
}

// isPinned: some pin of partition (s, p) is a prefix of k.
export function isPinned(pins: readonly Pin[], s: string, p: string, k: string): boolean {
  for (const x of pins) if (x.s === s && x.p === p && k.startsWith(x.prefix)) return true;
  return false;
}

// asPins is the stored pin list, dropping anything malformed.
function asPins(raw: unknown): Pin[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (x): x is Pin =>
      !!x && typeof x === "object" && typeof x.s === "string" && typeof x.p === "string" && typeof x.prefix === "string",
  );
}

// CoverageRecord marks a prefix whose complete listing is known: a key under a
// `complete` prefix with no entry is known absent.
export interface CoverageRecord {
  s: MirrorStoreName;
  p: string;
  prefix: string;
  complete: boolean;
  fetchedAt: number;
}

// KeyRangeSpec selects keys under `prefix` between lo and hi. An empty lo / hi
// means "unbounded on that side" (keys are never empty). Keys in `keep` are
// spared.
export interface KeyRangeSpec {
  prefix: string;
  lo: string;
  loOpen: boolean;
  hi: string;
  hiOpen: boolean;
  keep?: ReadonlySet<string>;
}

// ListRow is one server-acknowledged list / getBatch row to mirror. value is
// present only when the row brought its bytes (a batch row with a crc).
export interface ListRow {
  k: string;
  crc32c: string;
  contentType: string;
  size: number;
  owner?: string;
  value?: ArrayBuffer;
}

// ListPageWrite is one list / getBatch page to fold into the mirror (see
// MirrorStore.applyListPage). range is the key range the page is
// authoritative for (null = none); complete marks the whole prefix known.
// seenFrom / seenTo are the page request's interval ([0, at] when unset).
export interface ListPageWrite {
  prefix: string;
  rows: ListRow[];
  range: KeyRangeSpec | null;
  complete: boolean;
  at: number;
  seenFrom?: number;
  seenTo?: number;
}

export type StoreHealth =
  | { available: true }
  | { available: false; reason: "unsupported" | "version_error" | "open_failed" };

export type OfflineStoreErrorReason =
  | "unsupported"
  | "version_error"
  | "open_failed"
  | "blocked"
  | "tx_failed"
  | "invalid_op";

// OfflineStoreError is an internal failure of an on-device store. detail
// carries the underlying DOMException name, when there is one.
export class OfflineStoreError extends Error {
  readonly reason: OfflineStoreErrorReason;
  readonly detail: string;
  constructor(reason: OfflineStoreErrorReason, detail = "") {
    super(`offline store: ${reason}${detail ? " (" + detail + ")" : ""}`);
    this.name = "OfflineStoreError";
    this.reason = reason;
    this.detail = detail;
  }
}

// OutboxFullError: adding the op would exceed the outbox's op or byte cap.
// Nothing was written.
export class OutboxFullError extends Error {
  readonly reason = "queue_full" as const;
  constructor() {
    super("offline outbox full");
    this.name = "OutboxFullError";
  }
}

export interface MirrorStore {
  // health probes the database (opening and closing it). A VersionError is
  // sticky for the life of this store object.
  health(): Promise<StoreHealth>;
  // get returns the entry (a tombstone included), or null. touch:true records
  // the access for LRU.
  get(s: MirrorStoreName, p: string, k: string, opts?: { touch?: boolean }): Promise<MirrorEntry | null>;
  // put folds the entry over the key's stored one (reconcile — "stored" when
  // the stored evidence is kept), evicting least-recently-used unpinned values
  // to fit maxBytes (see evict). Never throws: "stored" = written; "meta" = the
  // value is larger than maxBytes, or pinned values leave no room for it
  // ("no_room"), so the entry's metadata replaced the key's previous entry
  // with value null; "failed" = nothing written (a transaction error, or
  // storage quota still exhausted after one eviction retry) — the caller then
  // invalidates the key.
  put(e: MirrorEntry): Promise<PutResult>;
  // delete removes the key's entry. Only for a server "absent" — any other
  // loss goes through invalidate.
  delete(s: MirrorStoreName, p: string, k: string): Promise<void>;
  // invalidate marks k "unknown" as of now in one transaction: any entry
  // becomes unknown (its bytes freed; an unknown one is re-stamped); with no
  // entry, an unknown entry is created only when a complete coverage record
  // covers k (else no entry already reads as unknown).
  invalidate(s: MirrorStoreName, p: string, k: string): Promise<void>;
  // scan lists PRESENT entries under prefix in ascending key order, after the
  // exclusive `after`, at most `limit` (tombstones and unknown entries are
  // skipped).
  scan(s: MirrorStoreName, p: string, prefix: string, opts?: { after?: string; limit?: number }): Promise<MirrorEntry[]>;
  // scanMeta is scan without the values (dropped as each entry is read), and
  // with unknown entries included (state "unknown"); tombstones are skipped.
  scanMeta(s: MirrorStoreName, p: string, prefix: string, opts?: { after?: string; limit?: number }): Promise<MirrorMeta[]>;
  // deleteRange removes present and absent entries in the range; returns how many.
  deleteRange(s: MirrorStoreName, p: string, r: KeyRangeSpec): Promise<number>;
  // applyListPage folds one page in a single transaction: each row reconciles
  // as a present entry (its own bytes; else the previous bytes when the crc
  // still matches; else metadata-only), present and unknown entries in `range`
  // that are not rows are pruned when strictly older than the page
  // (tombstones stay), `complete` records the prefix's coverage — unless a
  // prune or row was skipped, or an unknown entry is left in the page's range
  // — then, if bytesUsed is over maxBytes, the page's own pinned values are
  // demoted to metadata-only from its last row while pinned values alone
  // exceed maxBytes ("no_room"), and LRU eviction (see evict) runs.
  applyListPage(s: MirrorStoreName, p: string, page: ListPageWrite): Promise<void>;
  getCoverage(s: MirrorStoreName, p: string, prefix: string): Promise<CoverageRecord | null>;
  putCoverage(c: CoverageRecord): Promise<void>;
  // isCoveredAbsent: some complete coverage record's prefix is a prefix of k.
  isCoveredAbsent(s: MirrorStoreName, p: string, k: string): Promise<boolean>;
  // deletePartition / deleteUserPartitions remove entries + coverage + pins
  // (every store) and return the number of entries removed. Other meta
  // records are never touched (only the byte counters are adjusted).
  deletePartition(p: string): Promise<number>;
  deleteUserPartitions(): Promise<number>;
  // deleteUserPartitionsExcept removes every per-user partition but
  // "u:<uid>" (the orphan sweep at enable); uid "" removes them all.
  deleteUserPartitionsExcept(uid: string): Promise<number>;
  getMeta<T>(key: string): Promise<T | undefined>;
  setMeta(key: string, v: unknown): Promise<void>;
  deleteMeta(key: string): Promise<void>;
  bytesUsed(): Promise<number>;
  setMaxBytes(n: number): void;
  // pins lists the pinned prefixes, oldest first.
  pins(): Promise<Pin[]>;
  // pin / unpin add / remove the pin (s, p, prefix) and recount pinnedBytes;
  // false when there was nothing to change.
  pin(s: MirrorStoreName, p: string, prefix: string): Promise<boolean>;
  unpin(s: MirrorStoreName, p: string, prefix: string): Promise<boolean>;
  // retainPins drops every pin whose partition is not in parts and recounts
  // pinnedBytes (the enable-time sweep); how many pins were dropped.
  retainPins(parts: readonly string[]): Promise<number>;
  // onEvict sets the sink (null: none) that receives the values each
  // transaction evicted, once it committed — never for an aborted one.
  onEvict(sink: ((evicted: readonly Evicted[]) => void) | null): void;
  // destroy deletes the whole database (never rejects; bounded by timeoutMs).
  // waitBlocked keeps waiting (up to timeoutMs) while another connection
  // blocks the delete, instead of resolving on `blocked`.
  destroy(timeoutMs?: number, waitBlocked?: boolean): Promise<void>;
}

export type OpKind = "put" | "delete" | "captureCreate" | "captureUpdate";
export type OpState = "queued" | "inflight" | "held" | "quarantined";

// OutboxOp is one queued write. Field rules:
//   - uid is never "" — capture ops use CAPTURE_UID;
//   - a delete carries bytes:null and crc32c:"";
//   - base: null = unknown (blind replay), "" = create-only (puts only),
//     otherwise the crc32c the write was based on (CAS);
//   - unseen (puts with base "" only): a create-only putIfMatch(k, v, "") of a
//     key the device had never seen — the server may hold a value, so a drop
//     restates nothing.
// revoked marks an op quarantined by a revocation (shorter TTL). writes counts
// the writes folded into the op (coalesce); a record without it reads as 1.
export interface OutboxOp {
  seq: number;
  opId: string;
  uid: string;
  store: "data" | "userdata" | "records" | "capture";
  op: OpKind;
  key: string;
  bytes: ArrayBuffer | null;
  contentType: string;
  crc32c: string;
  base: string | null;
  owner?: string;
  originTab: string;
  state: OpState;
  leaseUntil: number;
  attempts: number;
  lastError: string;
  createdAt: number;
  heldSince: number;
  quarantinedAt: number;
  revoked?: true;
  unseen?: true;
  writes: number;
}

export type NewOutboxOp = Omit<OutboxOp, "seq">;
export type NewOp = NewOutboxOp;

export interface OutboxStats {
  ops: number;
  bytes: number;
  queued: number;
  inflight: number;
  held: number;
  quarantined: number;
}

export interface OutboxCore {
  health(): Promise<StoreHealth>;
  // add validates the op, enforces the caps (OutboxFullError, nothing written)
  // and resolves the new seq.
  add(op: NewOutboxOp): Promise<number>;
  get(seq: number): Promise<OutboxOp | null>;
  // update merges patch into the op; false when there is no such op.
  update(seq: number, patch: Partial<NewOutboxOp>): Promise<boolean>;
  remove(seq: number | readonly number[]): Promise<void>;
  // list / byKey resolve in ascending seq.
  list(f?: { uid?: string; states?: readonly OpState[] }): Promise<OutboxOp[]>;
  byKey(uid: string, store: OutboxOp["store"], key: string): Promise<OutboxOp[]>;
  stats(): Promise<OutboxStats>;
  // quarantineUid moves uid's queued / held / inflight ops to "quarantined"
  // (quarantinedAt at, lastError reason when given, lease dropped; seq,
  // heldSince and attempts kept). reason "revoked" also marks uid's already
  // quarantined ops revoked (their quarantinedAt kept). Capture ops are never
  // quarantined. Returns how many ops changed.
  quarantineUid(uid: string, at: number, o?: { revoked?: boolean; reason?: string }): Promise<number>;
  // claim leases a queued op (or an inflight op whose lease expired) as
  // inflight until at+ttlMs; false when it is not claimable.
  claim(seq: number, at: number, ttlMs: number): Promise<boolean>;
  // clear removes every op; returns how many.
  clear(): Promise<number>;
  // destroy deletes the database (see MirrorStore.destroy).
  destroy(timeoutMs?: number, waitBlocked?: boolean): Promise<void>;
}

// Swept is what Outbox.sweepExpired deleted, by bucket (never the bytes).
export interface Swept {
  quarantine: OutboxOp[];
  revoked: OutboxOp[];
  held: OutboxOp[];
}

// Outbox is the S4 core plus the queue operations the write path and the
// drain engine use. forKey / all are byKey / list().
export interface Outbox extends OutboxCore {
  forKey(uid: string, store: OutboxOp["store"], key: string): Promise<OutboxOp[]>;
  all(): Promise<OutboxOp[]>;
  countFor(uid: string): Promise<number>;
  // patch is the drain's write-back: one read-modify-write transaction that
  // merges fields into the op — a no-op (false) when the op is gone (sent,
  // swept, cleared: nothing is resurrected) or quarantined (quarantine is
  // sticky; only releaseQuarantine lifts it).
  patch(seq: number, fields: Partial<NewOutboxOp>): Promise<boolean>;
  // sweepExpired deletes the ops past their TTL (QUARANTINE_TTL_MS,
  // REVOKED_TTL_MS, HELD_TTL_MS) in one transaction and returns them.
  sweepExpired(at: number): Promise<Swept>;
  // enqueue adds n in one transaction, folding it into the key's latest op
  // when that op was never sent (coalesce): the op stored (with its new seq),
  // null when the fold cancelled both, "full" when the result would pass a
  // cap (nothing written).
  enqueue(n: NewOutboxOp): Promise<OutboxOp | null | "full">;
  // releaseQuarantine returns uid's quarantined ops — except revoked ones
  // (lastError "revoked": a re-added user gets a new uid) — to "queued", in
  // their original order; how many.
  releaseQuarantine(uid: string): Promise<number>;
  // claimHead leases the first sendable op of `uids` (by seq) as inflight
  // until at+leaseMs: "busy" when (unless force) another lease is live, null
  // when nothing is sendable. Quarantined ops, held ops not yet due and every
  // later op of a not-yet-due held op's (store, key) are skipped.
  claimHead(uids: readonly string[], at: number, leaseMs: number, force: boolean): Promise<OutboxOp | "busy" | null>;
}

// --- IndexedDB plumbing -------------------------------------------------------

export type Upgrade = (db: IDBDatabase, oldVersion: number, tx: IDBTransaction) => void;

// openDatabase opens name@version, running `upgrade` on a version change. The
// returned connection closes itself on `versionchange`. Rejects with
// OfflineStoreError: "version_error" when the database exists at a newer
// version, "blocked" when another connection holds up the upgrade for longer
// than 3 s, "open_failed" otherwise.
export function openDatabase(env: IdbEnv, name: string, version: number, upgrade: Upgrade): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const fail = (err: OfflineStoreError) => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      reject(err);
    };
    let req: IDBOpenDBRequest;
    try {
      req = env.factory.open(name, version);
    } catch (err) {
      fail(new OfflineStoreError("open_failed", errName(err)));
      return;
    }
    req.onupgradeneeded = (ev) => {
      const tx = req.transaction;
      try {
        if (tx) upgrade(req.result, ev.oldVersion, tx);
      } catch {
        try {
          tx?.abort();
        } catch {
          /* already finishing */
        }
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      if (settled) {
        // The blocked timeout already gave up on this open.
        db.close();
        return;
      }
      db.onversionchange = () => db.close();
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      resolve(db);
    };
    req.onerror = () => {
      const n = errName(req.error);
      fail(new OfflineStoreError(n === "VersionError" ? "version_error" : "open_failed", n));
    };
    req.onblocked = () => {
      if (timer === undefined && !settled) {
        timer = setTimeout(() => fail(new OfflineStoreError("blocked")), BLOCKED_OPEN_MS);
      }
    };
  });
}

// deleteDatabase deletes a database by name. It never rejects: it resolves on
// success, on `blocked` (the delete completes once the blocking connections
// close — unless waitBlocked, which keeps waiting), on error, on a synchronous
// throw, or after timeoutMs.
export function deleteDatabase(env: IdbEnv | null, name: string, timeoutMs = 1000, waitBlocked = false): Promise<void> {
  return new Promise<void>((resolve) => {
    if (!env) {
      resolve();
      return;
    }
    const timer = setTimeout(done, timeoutMs);
    function done() {
      clearTimeout(timer);
      resolve();
    }
    try {
      const req = env.factory.deleteDatabase(name);
      req.onsuccess = done;
      req.onerror = done;
      if (!waitBlocked) req.onblocked = done;
    } catch {
      done();
    }
  });
}

function errName(err: unknown): string {
  return err && typeof err === "object" && "name" in err ? String((err as { name: unknown }).name) : "";
}

// req adapts one IDB request to a promise. Only IDB request promises are ever
// awaited inside a transaction: their continuations run in the success event's
// microtask checkpoint, while the transaction is still active.
function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

// iterate walks a cursor request, calling fn (synchronously, so it may issue
// cursor.delete()/update()) for each row until fn returns false or rows run out.
function iterate(r: IDBRequest<IDBCursorWithValue | null>, fn: (c: IDBCursorWithValue) => boolean | void): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    r.onsuccess = () => {
      const c = r.result;
      if (!c || fn(c) === false) resolve();
      else c.continue();
    };
    r.onerror = () => reject(r.error);
  });
}

// runTx runs body in one transaction on db and closes db when it finishes. It
// resolves with body's value only once the transaction commits. If body throws,
// the transaction is aborted and body's error is the rejection (a raw DOM error
// is wrapped as OfflineStoreError("tx_failed", <its name>)); any other failure
// rejects with OfflineStoreError("tx_failed").
export function runTx<T>(
  db: IDBDatabase,
  names: string[],
  mode: IDBTransactionMode,
  body: (tx: IDBTransaction) => Promise<T>,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(names, mode);
    } catch (err) {
      db.close();
      reject(new OfflineStoreError("tx_failed", errName(err)));
      return;
    }
    let value: T;
    let bodyFailed = false;
    let bodyErr: unknown;
    const failed = (cause: unknown) => {
      db.close();
      reject(bodyFailed ? bodyErr : new OfflineStoreError("tx_failed", errName(cause)));
    };
    tx.oncomplete = () => {
      db.close();
      if (bodyFailed) reject(bodyErr);
      else resolve(value);
    };
    // A failed request's error event bubbles here before the abort (tx.error
    // isn't set yet), so read the request's own error for its name.
    tx.onerror = (ev) => failed((ev.target as IDBRequest | null)?.error ?? tx.error);
    tx.onabort = () => failed(tx.error);
    body(tx).then(
      (v) => {
        value = v;
      },
      (err) => {
        bodyFailed = true;
        bodyErr =
          err instanceof OfflineStoreError || err instanceof OutboxFullError
            ? err
            : new OfflineStoreError("tx_failed", errName(err));
        try {
          tx.abort();
        } catch {
          /* already committed or aborted */
        }
      },
    );
  });
}

// IdbHandle opens a store's database on demand (one connection per operation)
// and latches a VersionError for the life of the store object.
export interface IdbHandle {
  env: IdbEnv | null;
  health(): Promise<StoreHealth>;
  run<T>(names: string[], mode: IDBTransactionMode, body: (tx: IDBTransaction) => Promise<T>): Promise<T>;
}

export function idbHandle(idb: IdbEnv | null | undefined, name: string, version: number, upgrade: Upgrade): IdbHandle {
  const env = idb === undefined ? defaultIdbEnv() : idb;
  let latched: StoreHealth | null = env ? null : { available: false, reason: "unsupported" };
  const open = async (): Promise<IDBDatabase> => {
    if (latched && !latched.available) throw new OfflineStoreError(latched.reason);
    try {
      return await openDatabase(env as IdbEnv, name, version, upgrade);
    } catch (err) {
      if (err instanceof OfflineStoreError && err.reason === "version_error") {
        latched = { available: false, reason: "version_error" };
      }
      throw err;
    }
  };
  return {
    env,
    async health() {
      if (latched) return latched;
      try {
        (await open()).close();
        return { available: true };
      } catch {
        return latched ?? { available: false, reason: "open_failed" };
      }
    },
    async run(names, mode, body) {
      return runTx(await open(), names, mode, body);
    },
  };
}

// metaOf is e without its value (scanMeta), or null for a tombstone.
function metaOf(e: MirrorEntry): MirrorMeta | null {
  if (e.state === "absent") return null;
  const { value, ...rest } = normal(e);
  return { ...rest, hasValue: value !== null && value !== undefined };
}

function vlen(e: { value: ArrayBuffer | null } | undefined): number {
  return e && e.value ? e.value.byteLength : 0;
}

function blen(b: ArrayBuffer | null): number {
  return b ? b.byteLength : 0;
}

function num(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : 0;
}

// Ledger is one transaction's byte counters: used (bytesUsed), pinned
// (pinnedBytes, never above used) and the pins that decide what is pinned.
interface Ledger {
  used: number;
  pinned: number;
  pins: Pin[];
}

function ledger(used: unknown, pinned: unknown, pins: unknown): Ledger {
  const u = num(used);
  return { used: u, pinned: Math.min(num(pinned), u), pins: asPins(pins) };
}

// debit / credit move e's value bytes out of / into the ledger.
function debit(l: Ledger, e: MirrorEntry | undefined): void {
  const n = vlen(e);
  if (n === 0 || !e) return;
  l.used -= n;
  if (isPinned(l.pins, e.s, e.p, e.k)) l.pinned -= n;
}

function credit(l: Ledger, e: MirrorEntry): void {
  const n = vlen(e);
  l.used += n;
  if (n > 0 && isPinned(l.pins, e.s, e.p, e.k)) l.pinned += n;
}

// counters is what a ledger writes back: [bytesUsed, pinnedBytes].
function counters(l: Ledger): [number, number] {
  const u = Math.max(0, l.used);
  return [u, Math.min(Math.max(0, l.pinned), u)];
}

const isUserPart = (p: string) => p.startsWith("u:");

// noRoom demotes a list page's own new pinned values (own, in page order) to
// metadata-only, from the last, while the pinned values alone exceed max — the
// first value in stays. Returns the demoted entries (each reported "no_room").
function noRoom(l: Ledger, own: readonly MirrorEntry[], max: number, out: Evicted[]): MirrorEntry[] {
  const demoted: MirrorEntry[] = [];
  for (let i = own.length - 1; i >= 0 && l.pinned > max; i--) {
    const e = own[i] as MirrorEntry;
    if (!isPinned(l.pins, e.s, e.p, e.k)) continue;
    debit(l, e);
    demoted.push({ ...e, value: null });
    out.push({ s: e.s, p: e.p, k: e.k, reason: "no_room" });
  }
  return demoted;
}

// PinEdit is a pin-list edit: the new list (null: unchanged) and the answer.
type PinEdit<T> = { pins: Pin[] | null; result: T };

function addPin(pins: Pin[], s: MirrorStoreName, p: string, prefix: string): PinEdit<boolean> {
  if (pins.some((x) => x.s === s && x.p === p && x.prefix === prefix)) return { pins: null, result: false };
  return { pins: [...pins, { s, p, prefix, at: now() }], result: true };
}

function dropPin(pins: Pin[], s: MirrorStoreName, p: string, prefix: string): PinEdit<boolean> {
  const kept = pins.filter((x) => !(x.s === s && x.p === p && x.prefix === prefix));
  return kept.length === pins.length ? { pins: null, result: false } : { pins: kept, result: true };
}

// retain keeps the pins of parts; it always recounts (the enable-time sweep
// also heals a pinnedBytes an older bundle left behind).
function retain(pins: Pin[], parts: readonly string[]): PinEdit<number> {
  const kept = pins.filter((x) => parts.includes(x.p));
  return { pins: kept, result: pins.length - kept.length };
}

// fire hands a committed transaction's evictions to the sink (a throwing sink
// is ignored).
function fire(sink: ((e: readonly Evicted[]) => void) | null, out: Evicted[]): void {
  if (!sink || out.length === 0) return;
  try {
    sink(out);
  } catch {
    /* the store's work is done */
  }
}

// cmpKey orders [s,p,k]-style string tuples element-wise, the IndexedDB order
// for string array keys.
function cmpKey(a: readonly string[], b: readonly string[]): number {
  for (let i = 0; i < a.length && i < b.length; i++) {
    const x = a[i] as string;
    const y = b[i] as string;
    if (x !== y) return x < y ? -1 : 1;
  }
  return a.length - b.length;
}

function inRange(k: string, r: KeyRangeSpec): boolean {
  return (
    k.startsWith(r.prefix) &&
    (r.lo === "" || (r.loOpen ? k > r.lo : k >= r.lo)) &&
    (r.hi === "" || (r.hiOpen ? k < r.hi : k <= r.hi)) &&
    !(r.keep && r.keep.has(k))
  );
}

// listed is the entry a list-page row leaves behind: the row's own bytes when
// it brought them (and they fit maxBytes), else the previous bytes when the
// previous entry is present with the same non-empty crc, else metadata-only.
function listed(s: MirrorStoreName, p: string, r: ListRow, prev: MirrorEntry | undefined, at: number, max: number): MirrorEntry {
  const same = prev && prev.state === "present" && r.crc32c !== "" && prev.crc32c === r.crc32c;
  return {
    ...r,
    s,
    p,
    state: "present",
    value: r.value && r.value.byteLength <= max ? r.value : same ? prev.value : null,
    fetchedAt: at,
    accessedAt: at,
  };
}

// covers: some complete coverage record of partition (s, p) is a prefix of k.
function covers(covs: Iterable<CoverageRecord>, s: string, p: string, k: string): boolean {
  for (const c of covs) if (c.complete && c.s === s && c.p === p && k.startsWith(c.prefix)) return true;
  return false;
}

// unknown is the "nothing reliable" entry invalidate leaves at k.
function unknown(s: MirrorStoreName, p: string, k: string, at: number): MirrorEntry {
  return { s, p, k, state: "unknown", value: null, contentType: "", crc32c: "", size: 0, fetchedAt: at, accessedAt: at };
}

// normal is e as stored: an unknown entry never carries a value or a crc.
function normal(e: MirrorEntry): MirrorEntry {
  return e.state === "unknown" ? { ...e, value: null, crc32c: "" } : e;
}

// seen is e's evidence interval [seenFrom, seenTo]; an entry without one
// reads as [0, fetchedAt].
export function seen(e: Pick<MirrorEntry, "seenFrom" | "seenTo" | "fetchedAt">): [number, number] {
  return [e.seenFrom ?? 0, e.seenTo ?? e.fetchedAt];
}

// sameValue: a and b state one server value — present with the same non-empty
// crc, or both absent.
function sameValue(a: MirrorEntry, b: MirrorEntry): boolean {
  return a.state === b.state && (a.state === "absent" || (a.state === "present" && a.crc32c !== "" && a.crc32c === b.crc32c));
}

// reconcile is what the mirror holds once incoming evidence is folded over
// stored, by request interval: stored strictly
// older → incoming; incoming strictly older → stored (the same object);
// overlapping and the same value (records: and owner) → incoming over the
// union of both intervals; overlapping and different → "unknown" (never the
// newer crc). An incoming win of the same value without bytes keeps the
// stored bytes. The comparisons are strict: equal instants overlap.
export function reconcile(stored: MirrorEntry | null | undefined, incoming: MirrorEntry): MirrorEntry {
  if (!stored) return incoming;
  const [sf, st] = seen(stored);
  const [nf, nt] = seen(incoming);
  const keep = (e: MirrorEntry) => (!e.value && sameValue(stored, e) && stored.value ? { ...e, value: stored.value } : e);
  if (st < nf) return keep(incoming);
  if (nt < sf) return stored;
  const u = { seenFrom: Math.min(sf, nf), seenTo: Math.max(st, nt) };
  return sameValue(stored, incoming) && (stored.owner ?? "") === (incoming.owner ?? "")
    ? keep({ ...incoming, ...u })
    : { ...unknown(incoming.s, incoming.p, incoming.k, incoming.fetchedAt), ...u };
}

// sweep is what a list page does to a stored entry it did not list: "prune"
// a present or unknown entry in its range that is strictly older than the
// page, "skip" one that is not (the page can't vouch for it), else "keep"
// (a tombstone, a row, out of range). With no range nothing is pruned; only
// an unknown entry in the prefix is then a skip.
function sweep(e: MirrorEntry, pg: ListPageWrite, keep: ReadonlySet<string>, from: number): "prune" | "skip" | "keep" {
  const r = pg.range;
  if (e.state === "absent" || keep.has(e.k) || !inRange(e.k, r ?? { prefix: pg.prefix, lo: "", loOpen: false, hi: "", hiOpen: false })) {
    return "keep";
  }
  if (!r) return e.state === "unknown" ? "skip" : "keep";
  return seen(e)[1] < from ? "prune" : "skip";
}

// pageSpan is a page's evidence interval ([0, at] when unset).
const pageSpan = (pg: ListPageWrite): [number, number] => [pg.seenFrom ?? 0, pg.seenTo ?? pg.at];

function validateOp(op: NewOutboxOp): void {
  if (
    op.uid === "" ||
    (op.op === "delete" && (op.bytes !== null || op.crc32c !== "" || op.base === "")) ||
    (op.base === "" && op.op !== "put") ||
    (op.unseen && op.base !== "")
  ) {
    throw new OfflineStoreError("invalid_op");
  }
}

function tally(ops: readonly OutboxOp[]): OutboxStats {
  const s: OutboxStats = { ops: ops.length, bytes: 0, queued: 0, inflight: 0, held: 0, quarantined: 0 };
  for (const o of ops) {
    s.bytes += blen(o.bytes);
    s[o.state]++;
  }
  return s;
}

function quarantined(op: OutboxOp, at: number, o: { revoked?: boolean; reason?: string }): OutboxOp {
  const next: OutboxOp = { ...op, state: "quarantined", quarantinedAt: at, leaseUntil: 0 };
  if (o.revoked) next.revoked = true;
  if (o.reason !== undefined) next.lastError = o.reason;
  return next;
}

// pendingCounts is [pending, quarantined, held] as offline.status() reports
// them for uid: its queued / inflight / held ops plus capture's, its
// quarantined ops (no uid: capture's only, and none), and — of pending — the
// ops waiting on a server limit: every held op, plus each later op of the same
// uid, store and key queued behind one (claimHead skips those too). ops are in
// queue order (Outbox.all()).
export function pendingCounts(ops: readonly OutboxOp[], uid: string): [number, number, number] {
  let p = 0;
  let q = 0;
  let h = 0;
  const behind = new Set<string>();
  for (const o of ops) {
    if (o.state === "quarantined") {
      q += +(uid !== "" && o.uid === uid);
      continue;
    }
    if ((uid === "" || o.uid !== uid) && o.uid !== CAPTURE_UID) continue;
    p++;
    const sk = o.uid + "\0" + o.store + "\0" + o.key;
    if (o.state === "held") behind.add(sk);
    if (behind.has(sk)) h++;
  }
  return [p, q, h];
}

function claimable(op: OutboxOp, at: number): boolean {
  return op.state === "queued" || (op.state === "inflight" && op.leaseUntil <= at);
}

// --- outbox: the queue rules both implementations share ----------------------------

let capOps = OUTBOX_MAX_OPS;
let capBytes = OUTBOX_MAX_BYTES;

// _setOutboxCapsForTest replaces the default caps (an outbox created with its
// own maxOps / maxBytes keeps them).
export function _setOutboxCapsForTest(ops: number, bytes: number): void {
  capOps = ops;
  capBytes = bytes;
}

// coalesce folds a never-sent op p with the later write n to the same key: n's
// write keeps p's base (the earliest), its unseen flag, identity and age. A
// create-only put (unseen or not) followed by a delete cancels out (null).
// Capture (CON-4): a captureCreate absorbing a captureUpdate stays a
// captureCreate carrying the update's bytes, content type and crc; of two
// updates the later wins.
export function coalesce(p: NewOutboxOp, n: NewOutboxOp): NewOutboxOp | null {
  if (p.op === "put" && p.base === "" && n.op === "delete") return null;
  const r: NewOutboxOp = {
    ...n,
    op: p.op === "captureCreate" ? p.op : n.op,
    base: p.base,
    opId: p.opId,
    originTab: p.originTab,
    writes: (p.writes || 1) + 1,
    createdAt: p.createdAt,
  };
  delete r.unseen;
  if (p.unseen) r.unseen = true;
  return r;
}

// Tbl is one readwrite transaction's view of the ops table, for the
// operations both implementations share: all() reads every op (ascending
// seq), walk() visits them in seq order until fn returns false (so a claim
// reads only up to the op it takes), inflight() reads the inflight ops alone
// (the state index), and put / del / add write.
interface Tbl {
  all(): Promise<OutboxOp[]>;
  walk(fn: (o: OutboxOp) => boolean | void): Promise<void>;
  inflight(): Promise<OutboxOp[]>;
  put(op: OutboxOp): void;
  del(seq: number): void;
  add(op: NewOutboxOp): Promise<number>;
}

type RunTbl = <T>(fn: (t: Tbl) => T | Promise<T>) => Promise<T>;

async function enqueueIn(t: Tbl, n: NewOutboxOp, maxOps: number, maxBytes: number): Promise<OutboxOp | null | "full"> {
  const ops = await t.all();
  // Only the key's LATEST op may absorb n, and only if it was never sent: a
  // fold never reorders a write around one already on the wire.
  let p: OutboxOp | undefined;
  for (const o of ops) if (o.uid === n.uid && o.store === n.store && o.key === n.key) p = o;
  if (p && (p.attempts > 0 || p.state === "inflight")) p = undefined;
  const rec = p ? coalesce(p, n) : n;
  const rest = ops.filter((o) => o !== p);
  const s = tally(rec ? [...rest, rec as OutboxOp] : rest);
  if (s.ops > maxOps || s.bytes > maxBytes) return "full";
  if (p) t.del(p.seq);
  return rec && { ...rec, seq: await t.add(rec) };
}

async function claimHeadIn(t: Tbl, uids: readonly string[], at: number, leaseMs: number, force: boolean): Promise<OutboxOp | "busy" | null> {
  if (!force && (await t.inflight()).some((o) => o.leaseUntil > at)) return "busy";
  const held = new Set<string>();
  let c: OutboxOp | null = null;
  await t.walk((o) => {
    const sk = o.store + "\0" + o.key;
    if (!uids.includes(o.uid) || o.state === "quarantined" || held.has(sk)) return;
    if (o.state === "held" && o.leaseUntil > at) {
      held.add(sk);
      return;
    }
    // queued, a held op now due, or a reclaimable inflight op (force, or its
    // lease ran out — a live lease already answered "busy").
    c = { ...o, state: "inflight", leaseUntil: at + leaseMs };
    return false;
  });
  if (c) t.put(c);
  return c;
}

// withQueue completes an outbox from its per-implementation core, its
// one-transaction primitive and its caps: the queue operations plus add and
// the quarantine moves, which both implementations share.
function withQueue(b: Omit<OutboxCore, "add" | "quarantineUid">, run: RunTbl, caps: () => number[]): Outbox {
  // remap replaces every op `pick` selects by `to(op)`; how many.
  const remap = (pick: (o: OutboxOp) => boolean, to: (o: OutboxOp) => OutboxOp) =>
    run(async (t) => {
      let n = 0;
      await t.walk((o) => {
        if (pick(o)) {
          t.put(to(o));
          n++;
        }
      });
      return n;
    });
  return {
    ...b,
    async add(op) {
      validateOp(op);
      const [ops, bytes] = caps() as [number, number];
      return run(async (t) => {
        const s = tally(await t.all());
        if (s.ops + 1 > ops || s.bytes + blen(op.bytes) > bytes) throw new OutboxFullError();
        return t.add(op);
      });
    },
    quarantineUid: (uid, at, q = {}) =>
      remap(
        (o) =>
          o.uid === uid &&
          uid !== CAPTURE_UID &&
          (o.state !== "quarantined" || (q.reason === "revoked" && o.lastError !== "revoked")),
        (o) => (o.state === "quarantined" ? { ...o, lastError: "revoked", revoked: true } : quarantined(o, at, q)),
      ),
    releaseQuarantine: (uid) =>
      remap(
        (o) => o.uid === uid && o.state === "quarantined" && o.lastError !== "revoked",
        (o) => {
          const c: OutboxOp = { ...o, state: "queued", quarantinedAt: 0, lastError: "" };
          delete c.revoked;
          return c;
        },
      ),
    sweepExpired: (at) =>
      run(async (t) => {
        const r: Swept = { quarantine: [], revoked: [], held: [] };
        await t.walk((o) => {
          const rv = o.revoked === true || o.lastError === "revoked";
          const q = o.state === "quarantined";
          const ttl = o.state === "held" ? HELD_TTL_MS : !q ? Infinity : rv ? REVOKED_TTL_MS : QUARANTINE_TTL_MS;
          if (at - ((q ? o.quarantinedAt : o.heldSince) || o.createdAt) < ttl) return;
          t.del(o.seq);
          (q ? (rv ? r.revoked : r.quarantine) : r.held).push({ ...o, bytes: null });
        });
        return r;
      }),
    forKey: b.byKey,
    all: () => b.list(),
    patch: (seq, f) => remap((o) => o.seq === seq && o.state !== "quarantined", (o) => ({ ...o, ...f, seq })).then((n) => n > 0),
    async countFor(uid) {
      return (await b.list({ uid })).length;
    },
    async enqueue(n) {
      validateOp(n);
      const [ops, bytes] = caps() as [number, number];
      return run((t) => enqueueIn(t, n, ops, bytes));
    },
    claimHead: (uids, at, leaseMs, force) => run((t) => claimHeadIn(t, uids, at, leaseMs, force)),
  };
}

// --- mirror: IndexedDB ------------------------------------------------------------

function upgradeMirror(db: IDBDatabase, oldVersion: number): void {
  if (oldVersion < 1) {
    const entries = db.createObjectStore(STORE_ENTRIES, { keyPath: ["s", "p", "k"] });
    entries.createIndex(IDX_ACCESSED_AT, "accessedAt");
    db.createObjectStore(STORE_COVERAGE, { keyPath: ["s", "p", "prefix"] });
    db.createObjectStore(STORE_META);
  }
}

export function createMirrorStore(o: { idb?: IdbEnv | null; maxBytes?: number } = {}): MirrorStore {
  const h = idbHandle(o.idb, MIRROR_DB_NAME, MIRROR_DB_VERSION, upgradeMirror);
  let maxBytes = o.maxBytes ?? DEFAULT_MAX_BYTES;
  let sink: ((e: readonly Evicted[]) => void) | null = null;
  const EM = [STORE_ENTRIES, STORE_COVERAGE, STORE_META];
  const kr = () => (h.env as IdbEnv).keyRange;
  // Every key [s,p,<any string>] of one partition.
  const partRange = (s: string, p: string) => kr().bound([s, p], [s, p, []]);
  const prefixRange = (s: string, p: string, prefix: string) => kr().bound([s, p, prefix], [s, p, prefix + "￿"]);

  // covered reports, inside tx, whether a complete coverage record covers k.
  const covered = async (tx: IDBTransaction, s: MirrorStoreName, p: string, k: string) => {
    let hit = false;
    await iterate(tx.objectStore(STORE_COVERAGE).openCursor(partRange(s, p)), (c) => {
      hit = covers([c.value as CoverageRecord], s, p, k);
      return !hit;
    });
    return hit;
  };

  // scanWith walks the entries under prefix in key order, after the exclusive
  // `after`, keeping pick's non-null results (at most `limit`) — scan and
  // scanMeta.
  const scanWith = <T>(
    s: MirrorStoreName,
    p: string,
    prefix: string,
    opts: { after?: string; limit?: number },
    pick: (e: MirrorEntry) => T | null,
  ) =>
    h.run([STORE_ENTRIES], "readonly", async (tx) => {
      const out: T[] = [];
      const limit = opts.limit ?? Infinity;
      const after = opts.after;
      if (limit <= 0) return out;
      const top = prefix + "￿";
      let range = prefixRange(s, p, prefix);
      if (after !== undefined && after > prefix) {
        // An `after` past every key under prefix leaves nothing to list.
        if (!after.startsWith(prefix) || after >= top) return out;
        range = kr().bound([s, p, after], [s, p, top], true, false);
      }
      await iterate(tx.objectStore(STORE_ENTRIES).openCursor(range), (c) => {
        const e = c.value as MirrorEntry;
        if (!e.k.startsWith(prefix) || (after !== undefined && e.k <= after)) return true;
        const x = pick(e);
        if (x !== null) out.push(x);
        return out.length < limit;
      });
      return out;
    });

  // readLedger / saveLedger read and write the byte counters inside tx.
  const readLedger = async (ms: IDBObjectStore) =>
    ledger(await req(ms.get(META_BYTES)), await req(ms.get(META_PINNED)), await req(ms.get(META_PINS)));
  const saveLedger = (ms: IDBObjectStore, l: Ledger) => {
    const [u, pn] = counters(l);
    ms.put(u, META_BYTES);
    ms.put(pn, META_PINNED);
  };

  // evict frees unpinned value bytes in LRU order (never skip) until l.used
  // is at most target, adding each victim to out: a covered victim is demoted
  // to metadata-only (its entry still proves the key present), an uncovered
  // one is deleted. False — having evicted nothing — when the pinned values
  // alone exceed target (no walk of the index); else whether it got there.
  const evict = async (tx: IDBTransaction, l: Ledger, target: number, out: Evicted[], reason: EvictReason, skip?: readonly string[]) => {
    if (l.used <= target) return true;
    if (l.pinned > target) return false;
    const covs = (await req(tx.objectStore(STORE_COVERAGE).getAll())) as CoverageRecord[];
    await iterate(tx.objectStore(STORE_ENTRIES).index(IDX_ACCESSED_AT).openCursor(), (c) => {
      if (l.used <= target) return false;
      const e = c.value as MirrorEntry;
      if (skip && cmpKey([e.s, e.p, e.k], skip) === 0) return;
      const n = vlen(e);
      if (n > 0 && !isPinned(l.pins, e.s, e.p, e.k)) {
        if (covers(covs, e.s, e.p, e.k)) c.update({ ...e, value: null });
        else c.delete();
        l.used -= n;
        out.push({ s: e.s, p: e.p, k: e.k, reason });
      }
      return l.used > target;
    });
    return l.used <= target;
  };

  const putOnce = async (incoming: MirrorEntry) => {
    const out: Evicted[] = [];
    const r = await h.run(EM, "readwrite", async (tx): Promise<PutResult> => {
      const es = tx.objectStore(STORE_ENTRIES);
      const ms = tx.objectStore(STORE_META);
      const key = [incoming.s, incoming.p, incoming.k];
      const prev = (await req(es.get(key))) as MirrorEntry | undefined;
      const entry = normal(reconcile(prev, incoming));
      if (entry === prev) return "stored"; // the stored evidence is newer
      const l = await readLedger(ms);
      debit(l, prev);
      const n = vlen(entry);
      const room = n <= maxBytes && (l.used + n <= maxBytes || (await evict(tx, l, maxBytes - n, out, "max_bytes", key)) || n === 0);
      if (!room) {
        // Oversize, or pinned values leave no room: the first value in stays.
        if (n <= maxBytes) out.push({ s: entry.s, p: entry.p, k: entry.k, reason: "no_room" });
        es.put({ ...entry, value: null });
        saveLedger(ms, l);
        return "meta";
      }
      es.put(entry);
      credit(l, entry);
      saveLedger(ms, l);
      return "stored";
    });
    fire(sink, out);
    return r;
  };

  // Removes every entry, coverage record and pin in the given ranges of each
  // store (pins: those gone selects), adjusting the byte counters. Returns the
  // entries removed.
  const deleteRanges = (ranges: (s: MirrorStoreName) => IDBKeyRange[], gone: (p: string) => boolean) =>
    h.run([STORE_ENTRIES, STORE_COVERAGE, STORE_META], "readwrite", async (tx) => {
      const es = tx.objectStore(STORE_ENTRIES);
      const ms = tx.objectStore(STORE_META);
      const l = await readLedger(ms);
      let count = 0;
      for (const s of MIRROR_STORES) {
        for (const range of ranges(s)) {
          await iterate(es.openCursor(range), (c) => {
            debit(l, c.value as MirrorEntry);
            c.delete();
            count++;
          });
          tx.objectStore(STORE_COVERAGE).delete(range);
        }
      }
      const kept = l.pins.filter((x) => !gone(x.p));
      if (kept.length !== l.pins.length) ms.put(kept, META_PINS);
      saveLedger(ms, l);
      return count;
    });

  // repin writes pins and recounts pinnedBytes exactly (the value bytes of
  // every entry under some pin), inside tx.
  const repin = async (tx: IDBTransaction, pins: Pin[]) => {
    const ms = tx.objectStore(STORE_META);
    const es = tx.objectStore(STORE_ENTRIES);
    const seenKeys = new Set<string>();
    let pinned = 0;
    for (const x of pins) {
      await iterate(es.openCursor(prefixRange(x.s, x.p, x.prefix)), (c) => {
        const e = c.value as MirrorEntry;
        const id = JSON.stringify([e.s, e.p, e.k]);
        if (!e.k.startsWith(x.prefix) || seenKeys.has(id)) return;
        seenKeys.add(id);
        pinned += vlen(e);
      });
    }
    ms.put(pins, META_PINS);
    ms.put(Math.min(pinned, num(await req(ms.get(META_BYTES)))), META_PINNED);
  };

  // editPins applies edit to the pin list in one transaction and, when it
  // changed something, rewrites the list and recounts; edit's result.
  const editPins = <T>(edit: (pins: Pin[]) => PinEdit<T>) =>
    h.run(EM, "readwrite", async (tx) => {
      const { pins, result } = edit(asPins(await req(tx.objectStore(STORE_META).get(META_PINS))));
      if (pins) await repin(tx, pins);
      return result;
    });

  return {
    health: h.health,
    get(s, p, k, opts = {}) {
      return h.run([STORE_ENTRIES], opts.touch ? "readwrite" : "readonly", async (tx) => {
        const es = tx.objectStore(STORE_ENTRIES);
        const e = (await req(es.get([s, p, k]))) as MirrorEntry | undefined;
        if (!e) return null;
        if (opts.touch) {
          e.accessedAt = now();
          es.put(e);
        }
        return e;
      });
    },
    async put(e) {
      const entry: MirrorEntry = normal({ ...e, accessedAt: e.accessedAt || now() });
      try {
        return await putOnce(entry);
      } catch (err) {
        if (!(err instanceof OfflineStoreError && err.detail === QUOTA_EXCEEDED)) return "failed";
      }
      // Storage quota exhausted: free at least a quarter of maxBytes in a
      // separate transaction, then retry once.
      // (Pinned values are spared: it frees what the unpinned ones allow.)
      try {
        const out: Evicted[] = [];
        await h.run(EM, "readwrite", async (tx) => {
          const ms = tx.objectStore(STORE_META);
          const l = await readLedger(ms);
          await evict(tx, l, Math.max(l.pinned, l.used - Math.ceil(maxBytes / 4)), out, "quota");
          saveLedger(ms, l);
        });
        fire(sink, out);
        return await putOnce(entry);
      } catch {
        return "failed";
      }
    },
    async invalidate(s, p, k) {
      await h.run(EM, "readwrite", async (tx) => {
        const es = tx.objectStore(STORE_ENTRIES);
        const ms = tx.objectStore(STORE_META);
        const prev = (await req(es.get([s, p, k]))) as MirrorEntry | undefined;
        if (!prev && !(await covered(tx, s, p, k))) return;
        es.put(unknown(s, p, k, now()));
        if (vlen(prev) === 0) return;
        const l = await readLedger(ms);
        debit(l, prev);
        saveLedger(ms, l);
      });
    },
    async delete(s, p, k) {
      await h.run(EM, "readwrite", async (tx) => {
        const es = tx.objectStore(STORE_ENTRIES);
        const ms = tx.objectStore(STORE_META);
        const prev = (await req(es.get([s, p, k]))) as MirrorEntry | undefined;
        if (!prev) return;
        es.delete([s, p, k]);
        const l = await readLedger(ms);
        debit(l, prev);
        saveLedger(ms, l);
      });
    },
    scan(s, p, prefix, opts = {}) {
      return scanWith(s, p, prefix, opts, (e) => (e.state === "present" ? e : null));
    },
    scanMeta(s, p, prefix, opts = {}) {
      return scanWith(s, p, prefix, opts, metaOf);
    },
    deleteRange(s, p, r) {
      return h.run(EM, "readwrite", async (tx) => {
        const ms = tx.objectStore(STORE_META);
        const l = await readLedger(ms);
        let count = 0;
        await iterate(tx.objectStore(STORE_ENTRIES).openCursor(prefixRange(s, p, r.prefix)), (c) => {
          const e = c.value as MirrorEntry;
          if (!inRange(e.k, r)) return;
          debit(l, e);
          c.delete();
          count++;
        });
        saveLedger(ms, l);
        return count;
      });
    },
    async applyListPage(s, p, pg) {
      const out: Evicted[] = [];
      await h.run([STORE_ENTRIES, STORE_COVERAGE, STORE_META], "readwrite", async (tx) => {
        const es = tx.objectStore(STORE_ENTRIES);
        const ms = tx.objectStore(STORE_META);
        const keep = new Set(pg.rows.map((r) => r.k));
        const [from, to] = pageSpan(pg);
        const l = await readLedger(ms);
        let clean = true;
        if (pg.range || pg.complete) {
          await iterate(es.openCursor(prefixRange(s, p, pg.prefix)), (c) => {
            const e = c.value as MirrorEntry;
            const w = sweep(e, pg, keep, from);
            if (w === "skip") clean = false;
            if (w !== "prune") return;
            debit(l, e);
            c.delete();
          });
        }
        const own: MirrorEntry[] = [];
        for (const r of pg.rows) {
          const prev = (await req(es.get([s, p, r.k]))) as MirrorEntry | undefined;
          const e = reconcile(prev, { ...listed(s, p, r, prev, pg.at, maxBytes), seenFrom: from, seenTo: to });
          if (e === prev || e.state === "unknown") clean = false;
          if (e === prev) continue;
          debit(l, prev);
          credit(l, e);
          if (vlen(e) > 0 && e.value !== prev?.value) own.push(e);
          es.put(e);
        }
        if (pg.complete && clean) {
          tx.objectStore(STORE_COVERAGE).put({ s, p, prefix: pg.prefix, complete: true, fetchedAt: pg.at });
        }
        for (const e of noRoom(l, own, maxBytes, out)) es.put(e);
        await evict(tx, l, maxBytes, out, "max_bytes");
        saveLedger(ms, l);
      });
      fire(sink, out);
    },
    getCoverage(s, p, prefix) {
      return h.run([STORE_COVERAGE], "readonly", async (tx) => {
        const c = (await req(tx.objectStore(STORE_COVERAGE).get([s, p, prefix]))) as CoverageRecord | undefined;
        return c ?? null;
      });
    },
    async putCoverage(c) {
      await h.run([STORE_COVERAGE], "readwrite", async (tx) => {
        tx.objectStore(STORE_COVERAGE).put(c);
      });
    },
    isCoveredAbsent(s, p, k) {
      return h.run([STORE_COVERAGE], "readonly", (tx) => covered(tx, s, p, k));
    },
    deletePartition(p) {
      return deleteRanges((s) => [partRange(s, p)], (x) => x === p);
    },
    deleteUserPartitions() {
      // Every partition "u:<uid>": [s,"u:"] ≤ key < [s,"u;"] (";" follows ":").
      return deleteRanges((s) => [kr().bound([s, "u:"], [s, "u;"], false, true)], isUserPart);
    },
    deleteUserPartitionsExcept(uid) {
      // Both sides of partition u: [s,"u:"] ≤ key < [s,u] (every [s,u,k] sorts
      // after [s,u]), and [s,u,[]] < key < [s,"u;"] (an array sorts after
      // every string key).
      const u = "u:" + uid;
      return deleteRanges(
        (s) =>
          uid === ""
            ? [kr().bound([s, "u:"], [s, "u;"], false, true)]
            : [kr().bound([s, "u:"], [s, u], false, true), kr().bound([s, u, []], [s, "u;"], true, true)],
        (x) => isUserPart(x) && (uid === "" || x !== u),
      );
    },
    getMeta<T>(key: string) {
      return h.run([STORE_META], "readonly", async (tx) => (await req(tx.objectStore(STORE_META).get(key))) as T | undefined);
    },
    async setMeta(key, v) {
      await h.run([STORE_META], "readwrite", async (tx) => {
        tx.objectStore(STORE_META).put(v, key);
      });
    },
    async deleteMeta(key) {
      await h.run([STORE_META], "readwrite", async (tx) => {
        tx.objectStore(STORE_META).delete(key);
      });
    },
    bytesUsed() {
      return h.run([STORE_META], "readonly", async (tx) => num(await req(tx.objectStore(STORE_META).get(META_BYTES))));
    },
    setMaxBytes(n) {
      maxBytes = n;
    },
    pins() {
      return h.run([STORE_META], "readonly", async (tx) => asPins(await req(tx.objectStore(STORE_META).get(META_PINS))));
    },
    pin(s, p, prefix) {
      return editPins((pins) => addPin(pins, s, p, prefix));
    },
    unpin(s, p, prefix) {
      return editPins((pins) => dropPin(pins, s, p, prefix));
    },
    retainPins(parts) {
      return editPins((pins) => retain(pins, parts));
    },
    onEvict(f) {
      sink = f;
    },
    destroy(timeoutMs, waitBlocked) {
      return deleteDatabase(h.env, MIRROR_DB_NAME, timeoutMs, waitBlocked);
    },
  };
}

// --- mirror: in-memory ------------------------------------------------------------

export function createMemoryMirrorStore(o: { maxBytes?: number } = {}): MirrorStore {
  let maxBytes = o.maxBytes ?? DEFAULT_MAX_BYTES;
  let sink: ((e: readonly Evicted[]) => void) | null = null;
  const entries = new Map<string, MirrorEntry>();
  const coverage = new Map<string, CoverageRecord>();
  const meta = new Map<string, unknown>();
  const id = (parts: readonly string[]) => JSON.stringify(parts);
  const used = () => num(meta.get(META_BYTES));
  // book reads the byte counters; save writes them back.
  const book = () => ledger(meta.get(META_BYTES), meta.get(META_PINNED), meta.get(META_PINS));
  const save = (l: Ledger) => {
    const [u, pn] = counters(l);
    meta.set(META_BYTES, u);
    meta.set(META_PINNED, pn);
  };
  const inPart = (e: { s: string; p: string }, s: string, p: string) => e.s === s && e.p === p;
  // under lists the partition's entries under prefix that keep, in key order,
  // after the exclusive `after` (scan / scanMeta; a limit ≤ 0 lists nothing).
  const under = (s: string, p: string, prefix: string, opts: { after?: string; limit?: number }, keep: (e: MirrorEntry) => boolean) =>
    (opts.limit ?? Infinity) <= 0
      ? []
      : [...entries.values()]
          .filter((e) => inPart(e, s, p) && keep(e) && e.k.startsWith(prefix) && (opts.after === undefined || e.k > opts.after))
          .sort((a, b) => (a.k < b.k ? -1 : a.k > b.k ? 1 : 0));

  // evict mirrors the IndexedDB walk: accessedAt ascending, ties by [s,p,k],
  // pinned values spared; a covered victim is demoted to metadata-only, an
  // uncovered one deleted. False — evicting nothing — when the pinned values
  // alone exceed target.
  const evict = (l: Ledger, target: number, out: Evicted[], reason: EvictReason, skip?: string) => {
    if (l.used <= target) return true;
    if (l.pinned > target) return false;
    const lru = [...entries.values()].sort(
      (a, b) => a.accessedAt - b.accessedAt || cmpKey([a.s, a.p, a.k], [b.s, b.p, b.k]),
    );
    for (const e of lru) {
      if (l.used <= target) break;
      const key = id([e.s, e.p, e.k]);
      if (key === skip || vlen(e) === 0 || isPinned(l.pins, e.s, e.p, e.k)) continue;
      l.used -= vlen(e);
      if (covers(coverage.values(), e.s, e.p, e.k)) entries.set(key, { ...e, value: null });
      else entries.delete(key);
      out.push({ s: e.s, p: e.p, k: e.k, reason });
    }
    return l.used <= target;
  };

  const deleteWhere = (pred: (e: { s: string; p: string }) => boolean) => {
    let count = 0;
    const l = book();
    for (const [key, e] of entries) {
      if (!pred(e)) continue;
      entries.delete(key);
      debit(l, e);
      count++;
    }
    for (const [key, c] of coverage) if (pred(c)) coverage.delete(key);
    const kept = l.pins.filter((x) => !pred(x));
    if (kept.length !== l.pins.length) meta.set(META_PINS, kept);
    save(l);
    return count;
  };

  // editPins applies a pin-list edit and, when it changed something, stores
  // the list and recounts pinnedBytes exactly.
  const editPins = async <T>(edit: (pins: Pin[]) => PinEdit<T>) => {
    const { pins, result } = edit(asPins(structuredClone(meta.get(META_PINS))));
    if (pins) {
      let pinned = 0;
      for (const e of entries.values()) if (isPinned(pins, e.s, e.p, e.k)) pinned += vlen(e);
      meta.set(META_PINS, pins);
      meta.set(META_PINNED, Math.min(pinned, used()));
    }
    return result;
  };

  return {
    async health() {
      return { available: true };
    },
    async get(s, p, k, opts = {}) {
      const e = entries.get(id([s, p, k]));
      if (!e) return null;
      if (opts.touch) e.accessedAt = now();
      return structuredClone(e);
    },
    async put(e) {
      let incoming: MirrorEntry;
      try {
        incoming = normal(structuredClone({ ...e, accessedAt: e.accessedAt || now() }));
      } catch {
        return "failed";
      }
      const key = id([e.s, e.p, e.k]);
      const prev = entries.get(key);
      const entry = normal(reconcile(prev, incoming));
      if (entry === prev) return "stored"; // the stored evidence is newer
      const out: Evicted[] = [];
      const l = book();
      debit(l, prev);
      const n = vlen(entry);
      const room = n <= maxBytes && (l.used + n <= maxBytes || evict(l, maxBytes - n, out, "max_bytes", key) || n === 0);
      if (!room) {
        // Oversize, or pinned values leave no room: the first value in stays.
        if (n <= maxBytes) out.push({ s: entry.s, p: entry.p, k: entry.k, reason: "no_room" });
        entries.set(key, { ...entry, value: null });
        save(l);
        fire(sink, out);
        return "meta";
      }
      entries.set(key, entry);
      credit(l, entry);
      save(l);
      fire(sink, out);
      return "stored";
    },
    async invalidate(s, p, k) {
      const key = id([s, p, k]);
      const prev = entries.get(key);
      if (!prev && !covers(coverage.values(), s, p, k)) return;
      entries.set(key, unknown(s, p, k, now()));
      const l = book();
      debit(l, prev);
      save(l);
    },
    async delete(s, p, k) {
      const key = id([s, p, k]);
      const prev = entries.get(key);
      if (!prev) return;
      entries.delete(key);
      const l = book();
      debit(l, prev);
      save(l);
    },
    async scan(s, p, prefix, opts = {}) {
      return under(s, p, prefix, opts, (e) => e.state === "present")
        .slice(0, opts.limit ?? Infinity)
        .map((e) => structuredClone(e));
    },
    async scanMeta(s, p, prefix, opts = {}) {
      return under(s, p, prefix, opts, (e) => e.state !== "absent")
        .slice(0, opts.limit ?? Infinity)
        .map((e) => metaOf(structuredClone(e)) as MirrorMeta);
    },
    async deleteRange(s, p, r) {
      let count = 0;
      const l = book();
      for (const [key, e] of entries) {
        if (!inPart(e, s, p) || !inRange(e.k, r)) continue;
        entries.delete(key);
        debit(l, e);
        count++;
      }
      save(l);
      return count;
    },
    async applyListPage(s, p, pg) {
      const keep = new Set(pg.rows.map((r) => r.k));
      const [from, to] = pageSpan(pg);
      const out: Evicted[] = [];
      const l = book();
      let clean = true;
      for (const [key, e] of entries) {
        if (!inPart(e, s, p) || !e.k.startsWith(pg.prefix)) continue;
        const w = sweep(e, pg, keep, from);
        if (w === "skip") clean = false;
        if (w !== "prune") continue;
        entries.delete(key);
        debit(l, e);
      }
      const own: MirrorEntry[] = [];
      for (const r of pg.rows) {
        const key = id([s, p, r.k]);
        const prev = entries.get(key);
        const e = reconcile(prev, { ...listed(s, p, r, prev, pg.at, maxBytes), seenFrom: from, seenTo: to });
        if (e === prev || e.state === "unknown") clean = false;
        if (e === prev) continue;
        debit(l, prev);
        credit(l, e);
        if (vlen(e) > 0 && e.value !== prev?.value) own.push(e);
        entries.set(key, structuredClone(e));
      }
      if (pg.complete && clean) coverage.set(id([s, p, pg.prefix]), { s, p, prefix: pg.prefix, complete: true, fetchedAt: pg.at });
      for (const e of noRoom(l, own, maxBytes, out)) entries.set(id([e.s, e.p, e.k]), e);
      evict(l, maxBytes, out, "max_bytes");
      save(l);
      fire(sink, out);
    },
    async getCoverage(s, p, prefix) {
      const c = coverage.get(id([s, p, prefix]));
      return c ? structuredClone(c) : null;
    },
    async putCoverage(c) {
      coverage.set(id([c.s, c.p, c.prefix]), structuredClone(c));
    },
    async isCoveredAbsent(s, p, k) {
      return covers(coverage.values(), s, p, k);
    },
    async deletePartition(p) {
      return deleteWhere((e) => e.p === p);
    },
    async deleteUserPartitions() {
      return deleteWhere((e) => e.p.startsWith("u:"));
    },
    async deleteUserPartitionsExcept(uid) {
      return deleteWhere((e) => e.p.startsWith("u:") && (uid === "" || e.p !== "u:" + uid));
    },
    async getMeta<T>(key: string) {
      return (meta.has(key) ? structuredClone(meta.get(key)) : undefined) as T | undefined;
    },
    async setMeta(key, v) {
      meta.set(key, structuredClone(v));
    },
    async deleteMeta(key) {
      meta.delete(key);
    },
    async bytesUsed() {
      return used();
    },
    setMaxBytes(n) {
      maxBytes = n;
    },
    async pins() {
      return asPins(structuredClone(meta.get(META_PINS)));
    },
    pin(s, p, prefix) {
      return editPins((pins) => addPin(pins, s, p, prefix));
    },
    unpin(s, p, prefix) {
      return editPins((pins) => dropPin(pins, s, p, prefix));
    },
    retainPins(parts) {
      return editPins((pins) => retain(pins, parts));
    },
    onEvict(f) {
      sink = f;
    },
    async destroy() {
      entries.clear();
      coverage.clear();
      meta.clear();
    },
  };
}

// --- outbox: IndexedDB ------------------------------------------------------------

function upgradeOutbox(db: IDBDatabase, oldVersion: number): void {
  if (oldVersion < 1) {
    const ops = db.createObjectStore(STORE_OPS, { keyPath: "seq", autoIncrement: true });
    ops.createIndex(IDX_UID_STORE_KEY, ["uid", "store", "key"]);
    ops.createIndex(IDX_STATE, "state");
  }
}

export function createOutbox(o: { idb?: IdbEnv | null; maxOps?: number; maxBytes?: number } = {}): Outbox {
  const h = idbHandle(o.idb, OUTBOX_DB_NAME, OUTBOX_DB_VERSION, upgradeOutbox);
  const caps = () => [o.maxOps ?? capOps, o.maxBytes ?? capBytes];
  const OPS = [STORE_OPS];
  const all = (tx: IDBTransaction) => req(tx.objectStore(STORE_OPS).getAll()) as Promise<OutboxOp[]>;
  const run: RunTbl = (fn) =>
    h.run(OPS, "readwrite", async (tx) => {
      const os = tx.objectStore(STORE_OPS);
      return fn({
        all: () => all(tx),
        walk: (f) => iterate(os.openCursor(), (c) => f(c.value as OutboxOp)),
        inflight: () => req(os.index(IDX_STATE).getAll("inflight")) as Promise<OutboxOp[]>,
        put: (op) => void os.put(op),
        del: (seq) => void os.delete(seq),
        add: async (r) => {
          // The key is generated: a record carrying a seq (a re-added op) drops it.
          const rec: Partial<OutboxOp> = { ...r };
          delete rec.seq;
          return Number(await req(os.add(rec)));
        },
      });
    });

  return withQueue({
    health: h.health,
    get(seq) {
      return h.run(OPS, "readonly", async (tx) => {
        const op = (await req(tx.objectStore(STORE_OPS).get(seq))) as OutboxOp | undefined;
        return op ?? null;
      });
    },
    update(seq, patch) {
      return h.run(OPS, "readwrite", async (tx) => {
        const os = tx.objectStore(STORE_OPS);
        const op = (await req(os.get(seq))) as OutboxOp | undefined;
        if (!op) return false;
        os.put({ ...op, ...patch, seq });
        return true;
      });
    },
    async remove(seq) {
      const seqs = typeof seq === "number" ? [seq] : seq;
      await h.run(OPS, "readwrite", async (tx) => {
        const os = tx.objectStore(STORE_OPS);
        for (const s of seqs) os.delete(s);
      });
    },
    list(f = {}) {
      return h.run(OPS, "readonly", async (tx) =>
        (await all(tx)).filter(
          (op) => (f.uid === undefined || op.uid === f.uid) && (!f.states || f.states.includes(op.state)),
        ),
      );
    },
    byKey(uid, store, key) {
      return h.run(OPS, "readonly", async (tx) => {
        const idx = tx.objectStore(STORE_OPS).index(IDX_UID_STORE_KEY);
        return (await req(idx.getAll((h.env as IdbEnv).keyRange.only([uid, store, key])))) as OutboxOp[];
      });
    },
    stats() {
      return h.run(OPS, "readonly", async (tx) => tally(await all(tx)));
    },
    claim(seq, at, ttlMs) {
      return h.run(OPS, "readwrite", async (tx) => {
        const os = tx.objectStore(STORE_OPS);
        const op = (await req(os.get(seq))) as OutboxOp | undefined;
        if (!op || !claimable(op, at)) return false;
        os.put({ ...op, state: "inflight", leaseUntil: at + ttlMs });
        return true;
      });
    },
    clear() {
      return h.run(OPS, "readwrite", async (tx) => {
        const os = tx.objectStore(STORE_OPS);
        const n = await req(os.count());
        os.clear();
        return n;
      });
    },
    destroy(timeoutMs, waitBlocked) {
      return deleteDatabase(h.env, OUTBOX_DB_NAME, timeoutMs, waitBlocked);
    },
  }, run, caps);
}

// --- outbox: in-memory ------------------------------------------------------------

export function createMemoryOutbox(o: { maxOps?: number; maxBytes?: number } = {}): Outbox {
  const caps = () => [o.maxOps ?? capOps, o.maxBytes ?? capBytes];
  const ops = new Map<number, OutboxOp>();
  let lastSeq = 0;
  const sorted = () => [...ops.values()].sort((a, b) => a.seq - b.seq);
  const clones = () => sorted().map((op) => structuredClone(op));
  // run is not one transaction: another operation can interleave at an
  // await, so put only rewrites an op that still exists (as an IndexedDB
  // transaction would never see it vanish, nothing is resurrected).
  const run: RunTbl = async (fn) =>
    fn({
      all: async () => clones(),
      async walk(f) {
        for (const op of clones()) if (f(op) === false) return;
      },
      inflight: async () => clones().filter((op) => op.state === "inflight"),
      put: (op) => void (ops.has(op.seq) && ops.set(op.seq, structuredClone(op))),
      del: (seq) => void ops.delete(seq),
      add: async (r) => {
        const seq = ++lastSeq;
        ops.set(seq, structuredClone({ ...r, seq }));
        return seq;
      },
    });

  return withQueue({
    async health() {
      return { available: true };
    },
    async get(seq) {
      const op = ops.get(seq);
      return op ? structuredClone(op) : null;
    },
    async update(seq, patch) {
      const op = ops.get(seq);
      if (!op) return false;
      ops.set(seq, structuredClone({ ...op, ...patch, seq }));
      return true;
    },
    async remove(seq) {
      for (const s of typeof seq === "number" ? [seq] : seq) ops.delete(s);
    },
    async list(f = {}) {
      return sorted()
        .filter((op) => (f.uid === undefined || op.uid === f.uid) && (!f.states || f.states.includes(op.state)))
        .map((op) => structuredClone(op));
    },
    async byKey(uid, store, key) {
      return sorted()
        .filter((op) => op.uid === uid && op.store === store && op.key === key)
        .map((op) => structuredClone(op));
    },
    async stats() {
      return tally(sorted());
    },
    async claim(seq, at, ttlMs) {
      const op = ops.get(seq);
      if (!op || !claimable(op, at)) return false;
      ops.set(seq, { ...op, state: "inflight", leaseUntil: at + ttlMs });
      return true;
    },
    async clear() {
      const n = ops.size;
      ops.clear();
      return n;
    },
    async destroy() {
      ops.clear();
      lastSeq = 0;
    },
  }, run, caps);
}
